import { useEffect, useState } from 'react';
import { progression, type DailyRewardDto, type ProgressNews, type StarterQuestDto } from '../net/api';
import { Sheet } from './Sheet';
import { play } from '../audio/sound';

/**
 * "Welcome to the Bay": the first-day checklist in the corner of the screen.
 * Reloads whenever `version` changes (after any action that might tick a step).
 */
export function QuestCard({ version, compact, onChanged }: { version: number; compact?: boolean; onChanged: (news?: ProgressNews) => void }) {
  const [q, setQ] = useState<StarterQuestDto>();
  // Starts folded on small screens, and in the cottage and farm screens, so it doesn't cover them.
  const roomy = () => !compact && window.innerWidth > 900 && window.innerHeight > 640;
  const [open, setOpen] = useState(roomy);
  useEffect(() => setOpen(!compact && window.innerWidth > 900 && window.innerHeight > 640), [compact]);
  useEffect(() => {
    progression.quest().then(setQ, () => {});
  }, [version]);
  if (!q || !q.enabled || q.finished || q.skipped) return null;
  const done = q.steps.filter((s) => s.done).length;
  if (q.hidden) {
    return (
      <button className="quest-pill" onClick={() => void progression.setQuestHidden(false).then(setQ)} aria-label="Show the welcome guide">
        Welcome guide · {done}/{q.steps.length}
      </button>
    );
  }
  const next = q.steps.find((s) => !s.done);
  return (
    <aside className="quest-card" aria-labelledby="quest-title">
      <header className="quest-head">
        <button className="link-btn quest-toggle" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
          <strong id="quest-title">{q.title}</strong> <span className="muted">{done}/{q.steps.length}</span>
        </button>
        <button className="link-btn small-print" onClick={() => void progression.setQuestHidden(true).then((r) => { setQ(r); onChanged(); })}>Hide</button>
        <button
          className="link-btn small-print"
          onClick={() => {
            if (window.confirm('Skip the welcome guide? You can start it again from Your account.')) void progression.setQuestSkipped(true).then((r) => { setQ(r); onChanged(); });
          }}
        >
          Skip
        </button>
      </header>
      <div className="quest-bar" aria-hidden="true"><i style={{ width: `${(done / q.steps.length) * 100}%` }} /></div>
      {open && (
        <>
          <ol className="quest-steps">
            {q.steps.map((s) => (
              <li key={s.key} className={s.done ? 'done' : s === next ? 'next' : undefined}>
                <span className="quest-check" aria-hidden="true">{s.done ? '✓' : ''}</span>
                <span>
                  {s.name}
                  {s.done && <span className="visually-hidden"> (done)</span>}
                  {s === next && <span className="quest-hint">{s.hint}</span>}
                </span>
              </li>
            ))}
          </ol>
          <p className="muted small-print">
            {q.steps.every((s) => s.coins === q.stepReward.coins) ? `Each step: +${q.stepReward.coins} coins. ` : ''}
            Finish them all for +{q.finishReward.coins} coins{q.finishReward.item ? ` and the ${q.finishReward.item}` : ''}!
          </p>

        </>
      )}
    </aside>
  );
}

/** Account panel: start a skipped welcome guide again. */
export function GuideSetting({ onChanged }: { onChanged?: () => void }) {
  const [q, setQ] = useState<StarterQuestDto>();
  useEffect(() => {
    progression.quest().then(setQ, () => {});
  }, []);
  if (!q || !q.enabled || q.finished) return null;
  return (
    <p className="muted small-print">
      {q.skipped ? 'You skipped the welcome guide. ' : 'The welcome guide shows what to do first. '}
      <button className="link-btn" onClick={() => void progression.setQuestSkipped(!q.skipped).then((r) => { setQ(r); onChanged?.(); })}>
        {q.skipped ? 'Start it again' : 'Skip it'}
      </button>
    </p>
  );
}

/** The 7-day daily gift calendar. */
export function DailyGift({ onClose, onClaimed }: { onClose: () => void; onClaimed: (news?: ProgressNews) => void }) {
  const [d, setD] = useState<DailyRewardDto>();
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string }>();
  useEffect(() => {
    progression.daily().then(setD, (e: Error) => setMsg({ kind: 'err', text: e.message }));
  }, []);
  async function claim() {
    setBusy(true);
    try {
      const r = await progression.claimDaily();
      play('gift');
      setD(r.calendar);
      setMsg({ kind: 'ok', text: `Day ${r.day}: +${r.coins} coins${r.xp ? `, +${r.xp} XP` : ''}${r.item ? ` and the ${r.item}!` : '!'}` });
      onClaimed(r.progress);
    } catch (e) {
      setMsg({ kind: 'err', text: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }
  const hours = d ? Math.max(1, Math.ceil((new Date(d.resetsAt).getTime() - Date.now()) / 3_600_000)) : 0;
  return (
    <Sheet title="Daily gift" subtitle="Open a gift every day you visit. Missing a day is fine: your calendar waits for you." onClose={onClose}>
      {d && (
        <ol className="daily-grid">
          {d.days.map((day) => (
            <li key={day.day} className={`daily-day ${day.state}`}>
              <span className="daily-num">Day {day.day}</span>
              <span className="daily-prize">{day.coins} coins</span>
              {day.xp > 0 && <span className="small-print">+{day.xp} XP</span>}
              {day.item && <span className="small-print daily-item">+ {day.item}</span>}
              {day.state === 'claimed' && <span className="daily-tick" aria-label="opened">✓</span>}
            </li>
          ))}
        </ol>
      )}
      {msg && <p className={msg.kind === 'ok' ? 'note-ok' : 'form-error'} role="status">{msg.text}</p>}
      {d && !d.claimedToday && <button className="btn wide" disabled={busy} onClick={() => void claim()}>{busy ? 'Opening…' : "Open today's gift"}</button>}
      {d?.claimedToday && <p className="muted">Your next gift is ready in about {hours} hour{hours === 1 ? '' : 's'}.</p>}
    </Sheet>
  );
}
